import { useDisclosure, useToast } from "@chakra-ui/react";
import { db, functions } from "connections/firebase";
import {
  updateDoc,
  deleteDoc,
  collection,
  doc,
  addDoc,
} from "firebase/firestore";
import _ from "lodash";
import { FormikHelpers } from "formik";
import { houseConverter, IHouseFormPayload } from "interfaces/house";
import { useEffect, useState } from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { httpsCallable } from "firebase/functions";

const collection_name = "houses";
const initialValues: IHouseFormPayload = {
  blok: "",
  houseType: "",
  isConfigured: false,
  status: false,
};

export default function useHouse() {
  const toast = useToast();
  const [data, loading, error] = useCollectionData(
    collection(db, collection_name).withConverter(houseConverter),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );
  const { onClose: onCloseForm, onOpen, isOpen: isOpenForm } = useDisclosure();

  const [formValue, setFormValue] = useState<{
    id: string;
    initialValues: IHouseFormPayload;
  }>({
    id: "",
    initialValues,
  });

  const [loadingAction, setLoadingAction] = useState<null | string>(null);
  const getInstalledDoorLock = httpsCallable(functions, "getInstalledDoorLock");

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  const getHouseDetail = (id: string) => {
    if (data) {
      const selected = data.find((value) => value.id === id);
      if (selected) return selected;
    }

    return null;
  };

  const getDoorLockList = async () => {
    const doorLockList = await getInstalledDoorLock();
    console.log(doorLockList);
  };

  const addHouse = async (params: IHouseFormPayload) => {
    setLoadingAction("new");
    await addDoc(collection(db, collection_name), params);
    setLoadingAction(null);
  };

  const updateHouse = async (id: string, params: IHouseFormPayload) => {
    setLoadingAction(id);
    await updateDoc(doc(db, collection_name, id), params);
    setLoadingAction(null);
  };

  const updateStatusHouse = async (id: string, status: boolean) => {
    setLoadingAction(id);
    await updateDoc(doc(db, collection_name, id), {
      status,
    });
    setLoadingAction(null);
  };

  const deleteHouse = async (id: string) => {
    setLoadingAction(id);
    await deleteDoc(doc(db, collection_name, id));
    setLoadingAction(null);
  };

  const openEditForm = (id: string) => {
    onOpen();
    const detail = getHouseDetail(id);
    const initialValues = _.omit(detail, ["id"]);
    setFormValue({
      id,
      initialValues,
    });
  };

  const openAddForm = () => {
    onOpen();
    setFormValue({
      id: "new",
      initialValues,
    });
  };

  const onSubmitForm = async (
    values: IHouseFormPayload,
    formikHelpers: FormikHelpers<IHouseFormPayload>
  ) => {
    if (formValue.id === "new") {
      await addHouse(values);
    } else {
      console.log(values);
      await updateHouse(formValue.id, values);
    }

    onCloseForm();
    formikHelpers.resetForm();
  };

  return {
    data,
    loading,
    addHouse,
    updateStatusHouse,
    updateHouse,
    deleteHouse,
    selectedId: loadingAction,
    isLoadingAction: loadingAction !== null,
    openAddForm,
    openEditForm,
    getHouseDetail,
    formValue,
    isOpenForm,
    onCloseForm,
    onSubmitForm,
    getDoorLockList,
  };
}
